import words from 'lodash/words';
import h from './common';
import user from './user';

const STORAGE_KEY = 'storage_search';
const STORAGE_SEPARATOR_TOKEN = '~';

export const MAX_RECENT_SEARCH_STORED = 4;

export default class SearchUtil {
    private selectedProfileGuid: string | null;
    private _lastQueries: string[];
    private _autoSuggestionConfig: any;
    private _recentSearchCount: number;

    constructor() {
        this.selectedProfileGuid = user.getProfile()?.profileGuid || null;

        const storedQueries = h.getStoreItem(this.__getStorageKey());
        if (storedQueries) {
            this._lastQueries = storedQueries.split(STORAGE_SEPARATOR_TOKEN);
        } else {
            this._lastQueries = [];
        }
        this._autoSuggestionConfig = h.getSearchAutoSuggestionConfig();
        this._recentSearchCount = h.getMaxRecentSearchCount() || MAX_RECENT_SEARCH_STORED;
    }

    /**
     * Function to get the storage key.
     * @returns {string}
     */
    private __getStorageKey(): string {
        if (this.selectedProfileGuid != null) {
            return `${STORAGE_KEY}${STORAGE_SEPARATOR_TOKEN}${this.selectedProfileGuid}`;
        }
        return STORAGE_KEY;
    }

    /**
     * Function to match a search query.
     * @param query
     * @returns {number}
     */
    private __matchSearch(query: string): number {
        return this._lastQueries.indexOf(query);
    }

    /**
     * Function to get the last queries.
     * @param number
     * @returns {string[]}
     */
    getLastQueries(number: number): string[] {
        return this._lastQueries.slice(0, number);
    }

    /**
     * Function to clear the last queries.
     */
    clearLastQueries(): void {
        this._lastQueries.length = 0;
        h.deleteStore(this.__getStorageKey());
    }

    /**
     * Function to save a search query.
     * @param query
     */
    saveSearchQuery(query: string): void {
        let queryIdx;

        // Save query term.
        // We won't save the duplicate queries and only update the query position.
        if ((queryIdx = this.__matchSearch(query)) > -1) {
            this._lastQueries.splice(queryIdx, 1);
        }
        this._lastQueries.unshift(query);

        // Save the terms in the local storage if specified.
        h.setStoreItem(
            this.__getStorageKey(),
            this.getLastQueries(this._recentSearchCount).join(STORAGE_SEPARATOR_TOKEN)
        );
    }

    /**
     * Function to check if the text contains Arabic characters.
     * @param text
     * @returns {boolean}
     */
    hasArabicCharacters(text: string): boolean {
        const arabicRegex = /[\u0600-\u06ff]|[\u0750-\u077f]|[\ufb50-\ufc3f]|[\ufe70-\ufefc]/;
        return arabicRegex.test(text);
    }

    /**
     * Function to get the search language based on the text.
     * @param text
     * @returns {string}
     */
    getSearchLanguage(text: string): string {
        return this.hasArabicCharacters(text) ? 'ar' : 'en';
    }

    /**
     * Function to get limited auto suggestions.
     * @param suggestions
     * @returns {any[]}
     */
    getLimitedAutoSuggestions(suggestions: any[]): any[] {
        return suggestions.slice(0, this._autoSuggestionConfig.maxResultsOnUI);
    }

    /**
     * Function to check if auto suggestion should be cleared.
     * @param query
     * @returns {boolean}
     */
    shouldClearAutoSuggestion(query = ''): boolean {
        return query.length < this._autoSuggestionConfig.startCharIndex;
    }

    /**
     * Function to check if auto suggestion should be triggered.
     * @param query
     * @returns {boolean}
     */
    shouldTriggerAutoSuggestion(query = ''): boolean {
        if (query.length >= this._autoSuggestionConfig.startCharIndex) {
            const wordsCount = words(query).length;
            if (wordsCount <= this._autoSuggestionConfig.stopWordIndex) {
                return true;
            }
        }
        return false;
    }
}
